import React, { useState, useCallback, useMemo } from 'react';

const CORRECT_DATES = ['09.02.2025', '2025-02-09', '9.02.2025', '9.2.2025', '09.2.2025'];

const WRONG_MESSAGES = [
  'Hmm... to nie ta data. Spróbuj jeszcze raz, Blondasku.',
  'Blisko, ale nie. Pomyśl o naszej rocznicy.',
  'To nie jest ta specjalna data... Pamiętasz ją?',
  'Jeszcze jedna próba! Przypomnij sobie nasz wyjątkowy dzień.',
];

const FloatingHeart = ({
  delay,
  size,
  left,
  dark,
}: {
  delay: number;
  size: number;
  left: number;
  dark: boolean;
}): React.JSX.Element => (
  <div
    className="absolute pointer-events-none animate-float"
    style={{
      left: `${left}%`,
      animationDelay: `${delay}s`,
      fontSize: `${size}rem`,
      color: dark ? 'rgba(99, 102, 241, 0.15)' : 'rgba(99, 102, 241, 0.2)',
    }}
  >
    &#10084;
  </div>
);

const generateHearts = (n: number): Array<{ id: number; delay: number; size: number; left: number }> =>
  Array.from({ length: n }, (_, i) => ({
    id: i,
    delay: Math.random() * 10,
    size: Math.random() * 1.6 + 0.6,
    left: Math.random() * 100,
  }));

type PasswordGateProps = {
  dark: boolean;
  onSuccess: () => void;
  onAdminClick: () => void;
};

export const PasswordGate = ({ dark, onSuccess, onAdminClick }: PasswordGateProps): React.JSX.Element => {
  const [input, setInput] = useState('');
  const [shake, setShake] = useState(false);
  const [error, setError] = useState('');
  const [attempts, setAttempts] = useState(0);
  const hearts = useMemo(() => generateHearts(14), []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();
      if (CORRECT_DATES.includes(input.trim())) {
        onSuccess();
      } else {
        setShake(true);
        setError(WRONG_MESSAGES[attempts % WRONG_MESSAGES.length]);
        setAttempts((p) => p + 1);
        setTimeout(() => setShake(false), 600);
      }
    },
    [input, attempts, onSuccess]
  );

  return (
    <>
      <style>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          8% { opacity: 0.6; }
          92% { opacity: 0.6; }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%  { transform: translateX(-8px); }
          40%  { transform: translateX(8px); }
          60%  { transform: translateX(-5px); }
          80%  { transform: translateX(5px); }
        }
        .animate-float  { animation: float  14s ease-in-out infinite; }
        .animate-fadeIn { animation: fadeIn 0.7s ease-out forwards; }
        .animate-shake  { animation: shake  0.5s ease-in-out; }
      `}</style>

      <div
        className={`fixed inset-0 flex items-center justify-center overflow-hidden transition-colors duration-500 ${
          dark
            ? 'bg-gradient-to-br from-gray-950 via-indigo-950 to-gray-950'
            : 'bg-gradient-to-br from-indigo-100 via-blue-50 to-purple-50'
        }`}
      >
        {hearts.map((h) => (
          <FloatingHeart key={h.id} delay={h.delay} size={h.size} left={h.left} dark={dark} />
        ))}

        <div
          className={`z-10 mx-4 w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl shadow-xl p-6 md:p-10 transition-colors duration-500 animate-fadeIn ${
            dark ? 'bg-gray-900/80 backdrop-blur-md' : 'bg-white/80 backdrop-blur-md'
          }`}
        >
          <div className="text-center">
            <div className="text-5xl mb-5">📖</div>
            <h1
              className={`text-xl md:text-2xl font-light mb-4 leading-relaxed ${
                dark ? 'text-indigo-100' : 'text-gray-700'
              }`}
            >
              Pamiętniczek
            </h1>
            <p className={`text-base mb-2 font-light ${dark ? 'text-indigo-200/70' : 'text-gray-500'}`}>
              Jeżeli jesteś Blondaskiem, który chce przeczytać pamiętniczek,
            </p>
            <p className={`text-base mb-6 font-light ${dark ? 'text-indigo-200/70' : 'text-gray-500'}`}>
              podaj datę Naszej rocznicy.
            </p>

            <p className={`text-xs mb-4 ${dark ? 'text-indigo-300/40' : 'text-gray-400'}`}>
              Format: DD.MM.RRRR
            </p>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className={`transition-transform ${shake ? 'animate-shake' : ''}`}>
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="__ . __ . ____"
                  className={`w-full px-6 py-4 rounded-2xl border-2 focus:outline-none text-center text-lg font-light transition-colors ${
                    dark
                      ? 'bg-gray-800/60 border-indigo-800 focus:border-indigo-500 text-indigo-100 placeholder:text-gray-600'
                      : 'bg-white/70 border-indigo-200 focus:border-indigo-400 text-gray-700 placeholder:text-gray-400'
                  }`}
                />
              </div>

              {error && (
                <p
                  className={`text-sm font-light leading-relaxed whitespace-pre-line animate-fadeIn ${
                    dark ? 'text-indigo-300' : 'text-indigo-600'
                  }`}
                >
                  {error}
                </p>
              )}

              <button
                type="submit"
                className={`mt-6 px-8 py-3 rounded-full text-base font-light shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 ${
                  dark
                    ? 'bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white'
                    : 'bg-gradient-to-r from-indigo-400 to-purple-400 hover:from-indigo-500 hover:to-purple-500 text-white'
                }`}
              >
                Otwórz pamiętniczek
              </button>
            </form>

            <button
              onClick={onAdminClick}
              className={`mt-8 text-xs ${dark ? 'text-gray-600 hover:text-gray-500' : 'text-gray-400 hover:text-gray-500'} transition-colors`}
            >
              Panel admina
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
